'use client';

/* Don't use with SSR */

import { createContext, use, useEffect, useLayoutEffect } from 'react';
import ReactGA from 'react-ga4';

import {
  META_PIXEL_ID,
  ROUTES,
  type SignupIntent,
  type SignupPlan,
  STARTHUB_PIXEL_ENDPOINT,
  STARTHUB_PIXEL_HOSTS,
  STARTHUB_PIXEL_ID,
} from '@repo/common';
import { scheduleIdleTask } from '@repo/frontend-shared/utils';

import { getCookie, setCookie } from '../utils/cookie';
import type { FunnelStepName, FunnelStepPayloads } from '../utils/funnel-steps';
import { useCookieConsent } from './cookie-consent';
import {
  hasCookieConsent,
  hasExplicitCookieConsent,
  hasExplicitMarketingConsent,
  subscribeToConsent,
  syncTrackingConsentSignals,
} from './cookie-consent-engine';

interface AnalyticsContextProps {
  measurementId: string;
  metaPixelEnabled: boolean;
}

export const AnalyticsContext = createContext<AnalyticsContextProps | undefined>(undefined);

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

type AnalyticsPropertyValue = string | number | boolean | null | undefined;
type AnalyticsPayload<TPayload extends { [TKey in keyof TPayload]: AnalyticsPropertyValue }> = TPayload;
type SanitizedAnalyticsEventProperties = Record<string, string | number | boolean | null>;
type SignupMethod = 'email' | 'google' | 'linkedin' | 'oauth' | 'passkey';
type CheckoutType = 'mba' | 'mentor_session';
type TemplateType = 'document_template' | 'email_template';
type AnalyticsEventSignal = 'conversion' | 'engagement' | 'funnel' | 'monetization';

export interface AnalyticsEventPayloads {
  course_started: AnalyticsPayload<{ course_id: string }>;
  course_completed: AnalyticsPayload<{ course_id: string }>;
  login_completed: AnalyticsPayload<{ method: SignupMethod }>;
  lesson_completed: AnalyticsPayload<{ course_id: string; lesson_id: string }>;
  premium_viewed: AnalyticsPayload<{ surface: string }>;
  locked_content_viewed: AnalyticsPayload<{ surface: string }>;
  upgrade_clicked: AnalyticsPayload<{ surface: string }>;
  checkout_viewed: AnalyticsPayload<{ checkout_type: CheckoutType }>;
  signup_started: AnalyticsPayload<{
    method: SignupMethod;
    source: string;
  }>;
  signup_completed: AnalyticsPayload<{
    method: SignupMethod;
    user_id?: string;
    /** Free or premium: the intent the visitor signed up with. */
    signup_intent?: SignupIntent;
  }>;
  onboarding_started: AnalyticsPayload<{
    onboarding_type: string;
  }>;
  onboarding_step_completed: AnalyticsPayload<{
    onboarding_type: string;
    step_name: string;
    step_index: number;
    is_final_step: boolean;
  }>;
  onboarding_completed: AnalyticsPayload<{
    onboarding_type: string;
    steps_count: number;
  }>;
  checkout_started: AnalyticsPayload<{
    checkout_type: CheckoutType;
    payment_type?: string;
    product_id?: string;
    /** Major units charged now: the first payment for installments. */
    amount?: number;
    /** Major units the checkout commits the buyer to in total. Marketing OS reads this as the conversion value. */
    value?: number;
    /** Uppercase ISO 4217. */
    currency?: string;
  }>;
  checkout_declined: AnalyticsPayload<{
    /** The plan the buyer chose to continue with instead of paying. */
    signup_intent: 'free';
    /** The MBA plan whose checkout was left. */
    plan: SignupPlan;
  }>;
  paid_plan_selected: AnalyticsPayload<{
    plan: SignupPlan;
    value: number;
    currency: string;
  }>;
  checkout_session_created: AnalyticsPayload<{
    checkout_type: CheckoutType;
    payment_type?: string;
    product_id?: string;
    amount?: number;
    currency?: string;
  }>;
  content_viewed: AnalyticsPayload<{
    content_type: 'course' | 'landing_page';
    content_id?: string;
    title?: string;
    surface: string;
  }>;
  purchase_completed: AnalyticsPayload<{
    value: number;
    currency: string;
  }>;
  template_card_clicked: AnalyticsPayload<{
    template_type: TemplateType;
    content_id?: string;
    slug?: string;
    title: string;
    surface: string;
  }>;
  template_viewed: AnalyticsPayload<{
    template_type: TemplateType;
    content_id?: string;
    slug?: string;
    title: string;
    surface: string;
  }>;
  template_downloaded: AnalyticsPayload<{
    template_type: 'document_template';
    content_id?: string;
    slug?: string;
    title: string;
    attachment_id?: string;
  }>;
  ai_help_started: AnalyticsPayload<{
    source_id: string;
    source_type?: string;
  }>;
  partner_plan_change_requested: AnalyticsPayload<{
    partner_license_id: string;
    license_type: string;
  }>;
}

export type AnalyticsEventName = keyof AnalyticsEventPayloads;

interface AnalyticsEventDefinition {
  description: string;
  signal: AnalyticsEventSignal;
  trigger: string;
}

export const ANALYTICS_EVENT_DEFINITIONS = {
  course_started: {
    description: 'Course progress first starts.',
    signal: 'engagement',
    trigger: 'Persisted course creation receipt.',
  },
  course_completed: {
    description: 'Course first completes.',
    signal: 'conversion',
    trigger: 'Existing progress completion command receipt.',
  },
  login_completed: {
    description: 'Authenticated login succeeds.',
    signal: 'conversion',
    trigger: 'Successful login response or OAuth callback.',
  },
  lesson_completed: {
    description: 'Lesson is completed.',
    signal: 'engagement',
    trigger: 'Persisted incomplete-to-complete transition.',
  },
  premium_viewed: { description: 'Premium offer is shown.', signal: 'funnel', trigger: 'Premium offer mounts.' },
  locked_content_viewed: {
    description: 'Content access gate is shown.',
    signal: 'funnel',
    trigger: 'Access gate mounts.',
  },
  upgrade_clicked: { description: 'Visitor selects upgrade.', signal: 'funnel', trigger: 'Upgrade CTA click.' },
  checkout_viewed: { description: 'Checkout section is shown.', signal: 'funnel', trigger: 'Checkout section mounts.' },
  signup_started: {
    description: 'Visitor begins account creation through an available signup method.',
    signal: 'funnel',
    trigger: 'Signup form submit or social signup button click.',
  },
  signup_completed: {
    description: 'Visitor successfully creates a StartHub account.',
    signal: 'conversion',
    trigger: 'Registration mutation returns a user id.',
  },
  onboarding_started: {
    description: 'User starts a guided onboarding flow.',
    signal: 'funnel',
    trigger: 'Onboarding process starts for a selected onboarding type.',
  },
  onboarding_step_completed: {
    description: 'User completes a meaningful step inside onboarding.',
    signal: 'funnel',
    trigger: 'Onboarding step submission succeeds.',
  },
  onboarding_completed: {
    description: 'User completes all required onboarding steps.',
    signal: 'conversion',
    trigger: 'Final onboarding step submission succeeds.',
  },
  checkout_started: {
    description: 'User begins a paid checkout flow.',
    signal: 'monetization',
    trigger: 'User requests MBA enrollment checkout or mentor-session checkout.',
  },
  checkout_declined: {
    description: 'A buyer on the premium path leaves the MBA checkout for the free plan.',
    signal: 'funnel',
    trigger: 'Continue with the free plan click on the pricing payment step.',
  },
  paid_plan_selected: {
    description: 'Visitor chooses a paid MBA plan, before signup or login.',
    signal: 'monetization',
    trigger: 'Pay 6 Installments or One-Time Payment button click on a landing pricing card.',
  },
  checkout_session_created: {
    description: 'Payment provider checkout session is created for the user.',
    signal: 'monetization',
    trigger: 'Checkout session API call succeeds and returns checkout redirect data.',
  },
  content_viewed: {
    description: 'Visitor views a resolved course or the main platform landing page.',
    signal: 'engagement',
    trigger: 'Course data resolves successfully or the main landing route is viewed.',
  },
  purchase_completed: {
    description: 'Paid enrollment is confirmed by StartHub after checkout.',
    signal: 'conversion',
    trigger: 'Server-confirmed MBA payment or enrollment access is observed on checkout return.',
  },
  template_card_clicked: {
    description: 'Visitor selects a template from a customer-facing template list.',
    signal: 'engagement',
    trigger: 'Template card click navigates toward template detail content.',
  },
  template_viewed: {
    description: 'User views a specific template detail page.',
    signal: 'engagement',
    trigger: 'Template detail component renders with template metadata.',
  },
  template_downloaded: {
    description: 'User downloads a document template attachment.',
    signal: 'conversion',
    trigger: 'Document template attachment download link is clicked.',
  },
  ai_help_started: {
    description: 'User starts AI help from contextual product content.',
    signal: 'engagement',
    trigger: 'AI help button is clicked with source context.',
  },
  partner_plan_change_requested: {
    description: 'Partner requests a license plan change from billing.',
    signal: 'monetization',
    trigger: 'Partner billing plan-change request action is submitted.',
  },
} satisfies Record<AnalyticsEventName, AnalyticsEventDefinition>;

interface MetaEventOptions {
  eventID: string;
}
type MetaPixelArguments = [
  command: string,
  eventName: string,
  properties?: Record<string, string | number>,
  options?: MetaEventOptions,
];
interface MetaPixelFunction {
  (...args: MetaPixelArguments): void;
  callMethod?: (...args: MetaPixelArguments) => void;
  loaded: boolean;
  push: MetaPixelFunction;
  queue: MetaPixelArguments[];
  version: string;
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: MetaPixelFunction;
    _fbq?: MetaPixelFunction;
  }
}

let initializedMeasurementId: string | undefined;
/** Measurement id whose GA load waits for an idle slot; Google calls made meanwhile sit in `queuedGoogleCalls`. */
let scheduledMeasurementId: string | undefined;
let cancelScheduledBrowserAnalytics: (() => void) | undefined;
let queuedGoogleCalls: Array<() => void> = [];
let cancelScheduledMetaPixelScript: (() => void) | undefined;
let initializedMetaPixel = false;
let metaPixelEnabled = false;
let pendingRegistrationEventId: string | undefined;
let pendingRegistrationCategory: SignupIntent | undefined;
let lastTrackedGooglePagePath: string | undefined;
let lastTrackedMetaPagePath: string | undefined;
let lastTrackedMetaContentKey: string | undefined;
let pendingMetaPagePath: string | undefined;
let pendingMetaPageViewTimer: ReturnType<typeof setTimeout> | undefined;
let metaBeaconImage: HTMLImageElement | undefined;
let metaBeaconPathname: string | undefined;
const REDACTED_ANALYTICS_TOKEN = 'redacted';
const SENSITIVE_ANALYTICS_QUERY_PARAMS = [
  'token',
  'session_id',
  'checkout_session_id',
  'checkoutSessionId',
  'checkout-session-id',
];
const URL_BEARING_ANALYTICS_QUERY_PARAMS = ['redirect', 'return_to', 'returnTo', 'return_url', 'return-url'];
const MAX_ANALYTICS_NESTED_URL_DEPTH = 2;

/**
 * Routes whose rendered content must never reach a session recording.
 *
 * Distinct from the paths that get redacted from a page-view path. That list
 * is about a secret in the URL; this one is about what is on the screen —
 * amounts, card tails, invoice history, what a session costs. A page view can
 * be made safe by rewriting the string, a recording cannot.
 *
 * A pattern contributes everything before its first parameter, so
 * /mentor/:slug also claims MENTOR_AVALABILITY_DAYS and
 * MENTOR_COACHING_DASHBOARD, which show a schedule rather than a price. Losing
 * those two to the mask is the safe direction to be wrong in; the alternative
 * is teaching this file to resolve routes the way the router does.
 */
const SENSITIVE_RECORDING_ROUTES: readonly string[] = [
  ROUTES.PRICING,
  ROUTES.OFFER,
  '/mba', // Retired checkout URLs can still contain sensitive session references.
  ROUTES.BILLING,
  ROUTES.PARTNER_BILLING,
  ROUTES.PARTNER_LICENSE,
  ROUTES.BOOKING_SUCCESS,
  ROUTES.BOOKING_CANCEL,
  ROUTES.ONE_ON_ONE_COACHING,
  ROUTES.MENTOR_BY_SLUG_PATTERN,
];

/**
 * Clarity masks the node carrying this and everything under it, client-side,
 * before anything is uploaded. Recording lifecycle is controlled separately by
 * the public-page allowlist and Clarity start/stop commands.
 */
export const CLARITY_MASK_ATTRIBUTE = 'data-clarity-mask';
const META_PIXEL_SCRIPT_ID = 'meta-pixel-script';
const META_PIXEL_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
const META_PAGE_VIEW_STABILIZATION_MS = 500;

const PIXEL_COOKIE_NAMES = ['_sh_vid', '_sh_sid', '_sh_sts', '_sh_actor'] as const;
const PIXEL_SESSION_MS = 30 * 60 * 1000;
const PIXEL_VISITOR_SECONDS = 182 * 24 * 60 * 60;
const META_IMAGE_PIXEL_URL = 'https://www.facebook.com/tr';
const META_FBC_SUBDOMAIN_INDEX = 1;
const META_FBP_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const META_FBP_RANDOM_RANGE = 10_000_000_000;
const COOKIE_DOMAIN_ROOT = 'starthub.academy';
interface PixelUserIdentity {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}

let pixelActor: PixelUserIdentity | null = null;
let pixelIdentifiedKey: string | undefined;
let pixelLastPage: string | undefined;
let pixelIdentityRevision = 0;
let pixelActorHash: string | undefined;
const pixelRequests = new Set<AbortController>();
let pixelConsentCategories: string | undefined;
let pixelConsentSubscribed = false;

export function getMarketingOsConsent(): { consent: 'granted' | 'denied'; cats: string } {
  const analytics = hasExplicitCookieConsent('analytics');
  const marketing = hasExplicitMarketingConsent();
  return {
    consent: analytics || marketing ? 'granted' : 'denied',
    cats: [...(analytics ? ['analytics', 'recording'] : []), ...(marketing ? ['advertising'] : [])].join(','),
  };
}

function syncStartHubPixelConsent(): void {
  const { cats } = getMarketingOsConsent();
  if (cats === pixelConsentCategories) return;
  // Abort requests assembled under the previous choice, including in-flight identification.
  for (const request of pixelRequests) request.abort();
  pixelRequests.clear();
  pixelIdentityRevision += 1;
  pixelLastPage = undefined;
  if (
    (!cats && pixelConsentCategories) ||
    (!cats.includes('advertising') && (pixelConsentCategories?.includes('advertising') || readPixelCookie('_sh_actor')))
  ) {
    // Do not continue analytics-only tracking with a previously identified visitor.
    resetStartHubPixelIdentity();
  }
  pixelConsentCategories = cats;
}

function readPixelCookie(name: string): string | undefined {
  return getCookie(name) ?? undefined;
}

/** Meta browser identifiers, raw as stored. Marketing OS forwards them to Meta for event matching. */
function readMetaBrowserIdentifiers(): { fbp?: string; fbc?: string } {
  if (!hasExplicitMarketingConsent()) return {};
  const fbp = readPixelCookie('_fbp');
  const fbc = readPixelCookie('_fbc');
  return {
    ...(fbp && fbp.length <= 200 ? { fbp } : {}),
    ...(fbc && fbc.length <= 200 ? { fbc } : {}),
  };
}

function writePixelCookie(name: string, value: string, maxAge: number): void {
  if (
    !setCookie(name, value, {
      domain: '.starthub.academy',
      maxAgeSeconds: maxAge,
      path: '/',
      sameSite: 'Lax',
      secure: true,
    })
  ) {
    throw new Error('Browser rejected the analytics cookie write');
  }
}

export function resetStartHubPixelIdentity(): void {
  for (const request of pixelRequests) request.abort();
  pixelRequests.clear();
  pixelActor = null;
  pixelActorHash = undefined;
  pixelIdentifiedKey = undefined;
  pixelLastPage = undefined;
  pixelIdentityRevision += 1;
  if (!(isBrowser() && STARTHUB_PIXEL_HOSTS.includes(window.location.hostname))) return;
  try {
    for (const name of PIXEL_COOKIE_NAMES) writePixelCookie(name, '', 0);
  } catch {
    // No persistent identity can be used when storage is blocked.
    return;
  }
}

function getStartHubPixelContext() {
  if (!(isBrowser() && STARTHUB_PIXEL_HOSTS.includes(window.location.hostname))) return undefined;
  if (!pixelConsentSubscribed) {
    subscribeToConsent(syncStartHubPixelConsent);
    pixelConsentSubscribed = true;
  }
  syncStartHubPixelConsent();
  const consent = getMarketingOsConsent();
  if (consent.consent !== 'granted') return undefined;
  try {
    if (pixelActorHash && readPixelCookie('_sh_actor') !== pixelActorHash) return undefined;
    let visitorId = readPixelCookie('_sh_vid');
    if (!visitorId) {
      visitorId = `visitor_${crypto.randomUUID()}`;
      writePixelCookie('_sh_vid', visitorId, PIXEL_VISITOR_SECONDS);
    }
    // Never attach a contact to a page-only identifier when persistence failed.
    if (readPixelCookie('_sh_vid') !== visitorId) return undefined;
    let sessionId = readPixelCookie('_sh_sid');
    const timestamp = Number(readPixelCookie('_sh_sts'));
    const newSession = !(sessionId && timestamp) || Date.now() - timestamp >= PIXEL_SESSION_MS;
    if (newSession) sessionId = `session_${crypto.randomUUID()}`;
    if (!sessionId) return undefined;
    writePixelCookie('_sh_sid', sessionId, PIXEL_SESSION_MS / 1000);
    writePixelCookie('_sh_sts', String(Date.now()), PIXEL_SESSION_MS / 1000);
    if (readPixelCookie('_sh_sid') !== sessionId) return undefined;
    return {
      pixel_id: STARTHUB_PIXEL_ID,
      visitor_id: visitorId,
      session_id: sessionId,
      ...consent,
      stable: true,
      new_session: newSession,
    };
  } catch {
    return undefined;
  }
}

async function sendStartHubPixel(endpoint: 'track' | 'identify', payload: object): Promise<boolean> {
  if (getMarketingOsConsent().consent !== 'granted' || (endpoint === 'identify' && !hasExplicitMarketingConsent()))
    return false;
  // No retries or queued replay across consent withdrawal, logout or account switches.
  try {
    const controller = new AbortController();
    pixelRequests.add(controller);
    return await fetch(`${STARTHUB_PIXEL_ENDPOINT}/${endpoint}`, {
      method: 'POST',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      keepalive: true,
      headers: { 'content-type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then(response => {
        if (!response.ok && endpoint === 'identify') pixelIdentifiedKey = undefined;
        return response.ok;
      })
      .catch(() => {
        if (endpoint === 'identify') pixelIdentifiedKey = undefined;
        return false;
      })
      .finally(() => pixelRequests.delete(controller));
  } catch {
    return false;
  }
}

export async function syncStartHubPixelUser(user: PixelUserIdentity | null): Promise<void> {
  if (!(isBrowser() && STARTHUB_PIXEL_HOSTS.includes(window.location.hostname))) return;
  syncStartHubPixelConsent();
  if (!user) {
    if (pixelActor) resetStartHubPixelIdentity();
    return;
  }
  if (!hasExplicitMarketingConsent()) {
    if (pixelActor || readPixelCookie('_sh_actor')) resetStartHubPixelIdentity();
    return;
  }
  // A different tab has already replaced this actor's shared identity. Wait for
  // the auth owner to resolve the new user instead of reattaching stale PII.
  if (pixelActor?.id === user.id && pixelActorHash && readPixelCookie('_sh_actor') !== pixelActorHash) return;
  if (pixelActor && pixelActor.id !== user.id) resetStartHubPixelIdentity();
  pixelActor = user;
  let revision = pixelIdentityRevision;
  try {
    const identityBeforeHash = readPixelCookie('_sh_actor');
    const visitorBeforeHash = readPixelCookie('_sh_vid');
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(user.id));
    const actor = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
    if (revision !== pixelIdentityRevision || !hasExplicitMarketingConsent()) return;
    const previousActor = readPixelCookie('_sh_actor');
    if (previousActor !== identityBeforeHash || readPixelCookie('_sh_vid') !== visitorBeforeHash) return;
    if (previousActor && previousActor !== actor) {
      resetStartHubPixelIdentity();
      pixelActor = user;
      revision = pixelIdentityRevision;
    }
    const context = getStartHubPixelContext();
    if (!context) return;
    writePixelCookie('_sh_actor', actor, PIXEL_VISITOR_SECONDS);
    if (readPixelCookie('_sh_actor') !== actor) return;
    pixelActorHash = actor;
    const firstName = user.firstName?.trim() || undefined;
    const lastName = user.lastName?.trim() || undefined;
    const key = JSON.stringify([context.visitor_id, actor, user.email, firstName, lastName]);
    if (pixelIdentifiedKey === key) return;
    pixelIdentifiedKey = key;
    // Send current categories before identity so the collector can enforce the same choice.
    const consentRecorded = await trackStartHubPixel('PageView', {}, undefined, getCurrentAnalyticsPagePath(), true);
    if (
      !consentRecorded ||
      revision !== pixelIdentityRevision ||
      !hasExplicitMarketingConsent() ||
      readPixelCookie('_sh_vid') !== context.visitor_id ||
      readPixelCookie('_sh_actor') !== actor
    ) {
      pixelIdentifiedKey = undefined;
      return;
    }
    sendStartHubPixel('identify', {
      pixel_id: STARTHUB_PIXEL_ID,
      visitor_id: context.visitor_id,
      external_user_id: user.id,
      email: user.email,
      first_name: firstName,
      last_name: lastName,
    });
  } catch {
    pixelIdentifiedKey = undefined;
  }
}

async function trackStartHubPixel(
  name: string,
  properties: SanitizedAnalyticsEventProperties,
  eventId?: string,
  pagePath?: string,
  consentRefresh = false,
): Promise<boolean> {
  const context = getStartHubPixelContext();
  if (!context) return false;
  const path = sanitizeAnalyticsPathname(getAnalyticsPathAndSearch(pagePath ?? getCurrentAnalyticsPagePath()).pathname);
  const pageKey = `${context.visitor_id}:${context.session_id}:${path}:${context.cats}`;
  if (!consentRefresh && name === 'PageView' && pixelLastPage === pageKey) return true;
  if (name === 'PageView') pixelLastPage = pageKey;
  const safeProperties = Object.fromEntries(
    Object.entries(properties)
      .filter(([key]) => !['title', 'user_id'].includes(key))
      .map(([key, value]) => [key, key === 'currency' && typeof value === 'string' ? value.toUpperCase() : value]),
  );
  const utm: Record<string, string> = {};
  const search = new URLSearchParams(window.location.search);
  for (const key of [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
    'gclid',
    'ttclid',
    'li_fat_id',
  ]) {
    const value = search.get(key);
    if (
      hasExplicitMarketingConsent() &&
      value &&
      value.length <= 200 &&
      ![...value].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)
    )
      utm[key] = value;
  }
  const sent = sendStartHubPixel('track', {
    events: [
      {
        event_id: eventId ?? crypto.randomUUID(),
        name,
        occurred_at: new Date().toISOString(),
        properties: safeProperties,
        new_session: context.new_session,
        page: {
          url: `${window.location.origin}${path}`,
          path,
          domain: window.location.hostname,
          title: null,
          referrer: getStartHubPixelReferrer(),
          utm,
        },
        context: { ...context, ...readMetaBrowserIdentifiers() },
      },
    ],
  });
  if (!consentRefresh && name === 'PageView' && path === ROUTES.PRICING)
    trackStartHubPixel('PricingViewed', {}, undefined, path);
  return sent;
}

/** Referral attribution uses the same explicit marketing consent as campaign parameters. */
function getStartHubPixelReferrer(): string | null {
  if (!(hasExplicitMarketingConsent() && document.referrer)) return null;
  try {
    const referrer = new URL(document.referrer);
    if (!['http:', 'https:'].includes(referrer.protocol)) return null;
    // Keep the referring site and link path without credentials, arbitrary queries or fragments.
    const value = `${referrer.origin}${sanitizeAnalyticsPathname(referrer.pathname)}`;
    return value.length <= 2048 ? value : null;
  } catch {
    return null;
  }
}

const START_HUB_EVENT_NAMES: Partial<Record<AnalyticsEventName, string>> = {
  course_started: 'CourseStarted',
  course_completed: 'CourseCompleted',
  signup_completed: 'FreemiumSignup',
  login_completed: 'Login',
  lesson_completed: 'LessonCompleted',
  premium_viewed: 'PremiumViewed',
  locked_content_viewed: 'LockedContentViewed',
  upgrade_clicked: 'UpgradeClicked',
  checkout_viewed: 'CheckoutViewed',
  checkout_started: 'InitiateCheckout',
  // Marketing OS enrolls the buyer in the free sequence on this; Meta gets nothing.
  checkout_declined: 'FreePlanChosen',
  // The MBA click reports InitiateCheckout under the event id shared with fbq and our own CAPI.
  paid_plan_selected: 'InitiateCheckout',
};

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

export function configureMetaPixel(enabled: boolean): void {
  metaPixelEnabled = enabled;
}

function isAnalyticsPropertyValue(value: unknown): value is AnalyticsPropertyValue {
  return value === null || value === undefined || ['boolean', 'number', 'string'].includes(typeof value);
}

function cleanAnalyticsProperties(properties: object): SanitizedAnalyticsEventProperties {
  const cleanedProperties: SanitizedAnalyticsEventProperties = {};

  for (const [key, value] of Object.entries(properties)) {
    if (isAnalyticsPropertyValue(value) && value !== undefined) {
      cleanedProperties[key] = value;
    }
  }

  return cleanedProperties;
}

function getResetPasswordTokenIndex(segments: string[]): number | undefined {
  const resetPasswordIndex = segments.findIndex(segment => segment.toLowerCase() === 'reset-password');
  const tokenIndex = resetPasswordIndex + 1;

  if (resetPasswordIndex === -1 || tokenIndex >= segments.length || !segments[tokenIndex]) {
    return undefined;
  }

  return tokenIndex;
}

function sanitizeAnalyticsPathname(pathname: string): string {
  const segments = pathname.split('/');
  const tokenIndex = getResetPasswordTokenIndex(segments);

  if (tokenIndex === undefined) {
    return pathname;
  }

  segments[tokenIndex] = REDACTED_ANALYTICS_TOKEN;
  return segments.join('/');
}

function getAnalyticsPathAndSearch(pagePath: string): { pathname: string; search: string } {
  const [pathAndSearch] = pagePath.split('#');
  const queryIndex = pathAndSearch.indexOf('?');

  return {
    pathname: queryIndex === -1 ? pathAndSearch : pathAndSearch.slice(0, queryIndex),
    search: queryIndex === -1 ? '' : pathAndSearch.slice(queryIndex),
  };
}

function getSensitiveAnalyticsQueryParams(searchParams: URLSearchParams): string[] {
  return SENSITIVE_ANALYTICS_QUERY_PARAMS.filter(param => searchParams.has(param));
}

function redactAnalyticsSearchParams(searchParams: URLSearchParams, sensitiveParams: string[]): void {
  for (const param of sensitiveParams) {
    searchParams.set(param, REDACTED_ANALYTICS_TOKEN);
  }
}

function getSensitiveNestedAnalyticsQueryParams(searchParams: URLSearchParams, depth: number): string[] {
  if (depth >= MAX_ANALYTICS_NESTED_URL_DEPTH) {
    return [];
  }

  return URL_BEARING_ANALYTICS_QUERY_PARAMS.filter(param => {
    const value = searchParams.get(param);

    return value ? hasSensitiveAnalyticsPagePathAtDepth(value, depth + 1) : false;
  });
}

function redactNestedAnalyticsSearchParams(
  searchParams: URLSearchParams,
  sensitiveNestedParams: string[],
  depth: number,
): void {
  for (const param of sensitiveNestedParams) {
    const value = searchParams.get(param);

    if (value) {
      searchParams.set(param, sanitizeAnalyticsPagePathAtDepth(value, depth + 1));
    }
  }
}

function sanitizeAnalyticsSearch(search: string, depth: number): string {
  const searchParams = new URLSearchParams(search);
  const sensitiveParams = getSensitiveAnalyticsQueryParams(searchParams);
  const sensitiveNestedParams = getSensitiveNestedAnalyticsQueryParams(searchParams, depth);

  if (sensitiveParams.length === 0 && sensitiveNestedParams.length === 0) {
    return search;
  }

  redactAnalyticsSearchParams(searchParams, sensitiveParams);
  redactNestedAnalyticsSearchParams(searchParams, sensitiveNestedParams, depth);
  const sanitizedSearch = searchParams.toString();
  return sanitizedSearch ? `?${sanitizedSearch}` : '';
}

function hasSensitiveAnalyticsPathname(pathname: string): boolean {
  return getResetPasswordTokenIndex(pathname.split('/')) !== undefined;
}

function hasSensitiveAnalyticsSearch(search: string, depth: number): boolean {
  const searchParams = new URLSearchParams(search);

  return (
    getSensitiveAnalyticsQueryParams(searchParams).length > 0 ||
    getSensitiveNestedAnalyticsQueryParams(searchParams, depth).length > 0
  );
}

function sanitizeAnalyticsPagePathAtDepth(pagePath: string, depth: number): string {
  const { pathname, search } = getAnalyticsPathAndSearch(pagePath);

  return `${sanitizeAnalyticsPathname(pathname)}${sanitizeAnalyticsSearch(search, depth)}`;
}

export function sanitizeAnalyticsPagePath(pagePath: string): string {
  return sanitizeAnalyticsPagePathAtDepth(pagePath, 0);
}

export function getCurrentAnalyticsPagePath(): string {
  if (!isBrowser()) {
    return '/';
  }

  return sanitizeAnalyticsPagePath(window.location.pathname + window.location.search);
}

function hasSensitiveAnalyticsPagePathAtDepth(pagePath: string, depth: number): boolean {
  const { pathname, search } = getAnalyticsPathAndSearch(pagePath);

  return hasSensitiveAnalyticsPathname(pathname) || hasSensitiveAnalyticsSearch(search, depth);
}

export function hasSensitiveAnalyticsPagePath(pagePath: string): boolean {
  return hasSensitiveAnalyticsPagePathAtDepth(pagePath, 0);
}

/**
 * A parameterised route keeps only the part before its first parameter. A
 * root-level pattern would reduce to nothing and then claim every page in the
 * app, so it is dropped rather than allowed to mask the whole product.
 */
const SENSITIVE_RECORDING_PREFIXES: readonly string[] = SENSITIVE_RECORDING_ROUTES.flatMap(route => {
  const prefix = route.split('/:')[0].toLowerCase();
  return prefix.length > 1 ? [prefix] : [];
});

function isSensitiveRecordingPathname(pathname: string): boolean {
  // Lowercased on both sides: the router resolves a path without regard to
  // case, so /Settings/Billing renders the billing page and has to count as
  // one. Compared on a segment boundary so /mba does not claim /mba-brochure.
  const normalized = pathname.toLowerCase();

  return SENSITIVE_RECORDING_PREFIXES.some(prefix => normalized === prefix || normalized.startsWith(`${prefix}/`));
}

/**
 * Whether a recording of this page would capture something it should not.
 *
 * Broader than the page-view check: a URL carrying a token is unsafe to
 * record for the same reason it is unsafe to report, but a billing page is
 * unsafe to record while being perfectly fine to report.
 *
 * A sensitive path nested in a query parameter is deliberately not inherited
 * here. What matters is the page being rendered, and /login?redirect=/billing
 * renders the login form.
 */
export function hasSensitiveRecordingPagePath(pagePath: string): boolean {
  const { pathname, search } = getAnalyticsPathAndSearch(pagePath);

  return (
    isSensitiveRecordingPathname(pathname) ||
    hasSensitiveAnalyticsPathname(pathname) ||
    hasSensitiveAnalyticsSearch(search, 0)
  );
}

function getAnalyticsPageLocation(pagePath: string): string {
  return new URL(pagePath, window.location.origin).href;
}

export function initializeBrowserAnalytics(measurementId: string): void {
  if (!(isBrowser() && hasCookieConsent('analytics')) || initializedMeasurementId === measurementId) {
    return;
  }

  ReactGA.initialize(measurementId, {
    gtagOptions: {
      send_page_view: false,
    },
  });
  initializedMeasurementId = measurementId;
}

/**
 * Loads GA after the page's `load` event, in an idle slot, instead of during first render. Google calls made before
 * then are queued and replayed in order once GA is initialized, so no page view or event is lost. Consent is checked
 * again at replay. An event call forces the load at once (see `flushBrowserAnalytics`).
 */
export function requestBrowserAnalytics(measurementId: string): void {
  if (
    !(isBrowser() && hasCookieConsent('analytics')) ||
    initializedMeasurementId === measurementId ||
    scheduledMeasurementId === measurementId
  ) {
    return;
  }

  cancelScheduledBrowserAnalytics?.();
  scheduledMeasurementId = measurementId;
  cancelScheduledBrowserAnalytics = scheduleIdleTask(flushBrowserAnalytics);
}

/** Initializes GA now if a load is pending, then replays the queued Google calls. */
function flushBrowserAnalytics(): void {
  cancelScheduledBrowserAnalytics?.();
  cancelScheduledBrowserAnalytics = undefined;
  const measurementId = scheduledMeasurementId;
  scheduledMeasurementId = undefined;
  if (measurementId) {
    initializeBrowserAnalytics(measurementId);
  }

  const calls = queuedGoogleCalls;
  queuedGoogleCalls = [];
  if (initializedMeasurementId && hasCookieConsent('analytics')) {
    for (const call of calls) call();
  }
}

/** Queues a Google call while the GA load is pending. Returns false when the call should run directly. */
function queueGoogleCall(call: () => void): boolean {
  if (!scheduledMeasurementId || initializedMeasurementId) return false;
  queuedGoogleCalls.push(call);
  return true;
}

let clarityStopped = false;

export function initializeConsentAwareClarity(clarityId: string, initializeClarity: (clarityId: string) => void): void {
  if (!(isBrowser() && hasCookieConsent('analytics'))) {
    return;
  }

  const initialPagePath = window.location.pathname + window.location.search;
  if (!isClarityPublicPage(initialPagePath)) {
    return;
  }

  document.documentElement.setAttribute('data-clarity-mask', 'true');
  initializeClarity(clarityId);
  if (clarityStopped) {
    window.clarity?.('start');
  }
  clarityStopped = false;
  syncTrackingConsentSignals();
}

/** Only the public landing application may record, never the member/admin/partner apps. */
export function isClarityPublicPage(pagePath: string): boolean {
  if (!isBrowser() || document.body.dataset.app !== 'landing' || hasSensitiveRecordingPagePath(pagePath)) return false;
  const pathname = getAnalyticsPathAndSearch(pagePath).pathname.toLowerCase().replace(/\/$/, '') || '/';
  return (
    ['/', '/about-us', '/faculty', '/founder', '/freemium', '/premium'].includes(pathname) ||
    /^\/courses\/[^/]+$/.test(pathname) ||
    /^\/goodie-bag(?:\/[^/]+)?$/.test(pathname)
  );
}

export function stopClarityRecording(): void {
  if (isBrowser()) {
    window.clarity?.('stop');
    clarityStopped = true;
  }
}

function loadMetaPixelScript(): void {
  cancelScheduledMetaPixelScript?.();
  cancelScheduledMetaPixelScript = undefined;
  if (document.getElementById(META_PIXEL_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = META_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = META_PIXEL_SCRIPT_SRC;
  document.head.appendChild(script);
}

/**
 * Sets up the `fbq` stub (which queues every call, eventID included) and `init` right away, and appends fbevents.js
 * on the same tick. With `deferScript`, the script waits for an idle slot after `load` instead; Meta replays the
 * stub's queue when it arrives, so nothing queued meanwhile is lost.
 */
export function initializeMetaPixel(deferScript = false): void {
  if (!(metaPixelEnabled && isBrowser() && hasCookieConsent('marketing'))) {
    return;
  }

  if (!window.fbq) {
    const fbq: MetaPixelFunction = (...args: MetaPixelArguments): void => {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
        return;
      }
      fbq.queue.push(args);
    };
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    fbq.push = fbq;
    window.fbq = fbq;
    window._fbq = fbq;
  }

  if (!deferScript) {
    loadMetaPixelScript();
  } else if (!(document.getElementById(META_PIXEL_SCRIPT_ID) || cancelScheduledMetaPixelScript)) {
    cancelScheduledMetaPixelScript = scheduleIdleTask(loadMetaPixelScript);
  }

  if (!initializedMetaPixel) {
    window.fbq('init', META_PIXEL_ID);
    initializedMetaPixel = true;
  }
  if (pendingRegistrationEventId) {
    window.fbq(
      'track',
      'CompleteRegistration',
      { content_category: pendingRegistrationCategory ?? 'free' },
      { eventID: pendingRegistrationEventId },
    );
    pendingRegistrationEventId = undefined;
    pendingRegistrationCategory = undefined;
  }
}

function readSignupIntentCategory(value: unknown): SignupIntent {
  return value === 'premium' ? 'premium' : 'free';
}

function trackMetaEvent(
  eventName: string,
  properties?: Record<string, string | number>,
  eventId?: string,
  command: 'track' | 'trackCustom' = 'track',
): boolean {
  if (!(isBrowser() && metaPixelEnabled && hasCookieConsent('marketing'))) {
    return false;
  }
  // A page view can wait for the idle slot. A visitor action may be followed by a full-page redirect, so it loads now.
  initializeMetaPixel(eventName === 'PageView');
  if (!window.fbq) return false;
  if (eventId) {
    window.fbq(command, eventName, properties, { eventID: eventId });
    return true;
  }
  window.fbq(command, eventName, properties);
  return true;
}

function trackMetaAnalyticsEvent(
  name: AnalyticsEventName,
  properties: SanitizedAnalyticsEventProperties,
  eventId?: string,
  onMetaQueued?: () => void,
): void {
  if (name === 'signup_started') {
    trackMetaEvent('Lead');
    return;
  }
  if (name === 'signup_completed') {
    // OAuth callbacks can run before the page-view hook configures the Pixel.
    const registration = { content_category: readSignupIntentCategory(properties.signup_intent) };
    if (eventId && !metaPixelEnabled && hasCookieConsent('marketing')) {
      pendingRegistrationEventId = eventId;
      pendingRegistrationCategory = registration.content_category;
      return;
    }
    trackMetaEvent('CompleteRegistration', registration, eventId);
    return;
  }
  if (name === 'paid_plan_selected') {
    // Counted once per attempt, at the click, so visitors who drop at signup still count. The server reports the
    // same click under the same event id, so Meta keeps one of the two.
    if (
      trackMetaEvent(
        'InitiateCheckout',
        {
          currency: String(properties.currency).toUpperCase(),
          ...(typeof properties.value === 'number' ? { value: properties.value } : {}),
          ...(typeof properties.plan === 'string' ? { content_name: properties.plan } : {}),
        },
        eventId,
      )
    ) {
      onMetaQueued?.();
    }
    return;
  }
  if (name === 'onboarding_completed') {
    trackMetaEvent('OnboardingComplete', undefined, eventId, 'trackCustom');
    return;
  }
  if (name === 'content_viewed') {
    const contentKey = `${properties.content_type}:${properties.content_id ?? ''}:${properties.surface}`;
    if (lastTrackedMetaContentKey === contentKey) {
      return;
    }
    lastTrackedMetaContentKey = contentKey;
    trackMetaEvent('ViewContent', {
      ...(typeof properties.content_type === 'string' ? { content_type: properties.content_type } : {}),
      ...(typeof properties.content_id === 'string' ? { content_ids: properties.content_id } : {}),
      ...(typeof properties.title === 'string' ? { content_name: properties.title } : {}),
    });
    return;
  }
  if (name === 'purchase_completed') {
    if (typeof properties.value === 'number' && typeof properties.currency === 'string') {
      if (
        trackMetaEvent('Purchase', { value: properties.value, currency: properties.currency.toUpperCase() }, eventId)
      ) {
        onMetaQueued?.();
      }
    }
  }
}

function trackGooglePageView(pagePath: string): void {
  const pageLocation = getAnalyticsPageLocation(pagePath);

  const pageView = {
    hitType: 'pageview',
    page: pagePath,
    page_location: pageLocation,
    location: pageLocation,
    page_path: pagePath,
    page_title: document.title,
  };
  if (initializedMeasurementId) {
    ReactGA.send(pageView);
    return;
  }
  if (queueGoogleCall(() => ReactGA.send(pageView))) return;

  window.gtag?.('event', 'page_view', {
    page_location: pageLocation,
    page_path: pagePath,
    page_title: document.title,
  });
}

function trackMetaPageView(pagePath: string): void {
  if (!hasCookieConsent('marketing') || lastTrackedMetaPagePath === pagePath || pendingMetaPagePath === pagePath) {
    return;
  }

  // The beacon already recorded this document's first page view; later routes still go through fbq.
  if (metaBeaconPathname !== undefined) {
    const beaconPathname = metaBeaconPathname;
    metaBeaconPathname = undefined;
    if (getAnalyticsPathAndSearch(pagePath).pathname === beaconPathname) {
      lastTrackedMetaPagePath = pagePath;
      // The deferred script still loads and creates _fbp; it just has no first page view left to send.
      initializeMetaPixel(true);
      return;
    }
  }

  if (pendingMetaPageViewTimer) {
    clearTimeout(pendingMetaPageViewTimer);
  }
  pendingMetaPagePath = pagePath;
  pendingMetaPageViewTimer = setTimeout(() => {
    const stablePagePath = pendingMetaPagePath;
    pendingMetaPagePath = undefined;
    pendingMetaPageViewTimer = undefined;
    if (!stablePagePath) {
      return;
    }
    lastTrackedMetaPagePath = stablePagePath;
    trackMetaEvent('PageView');
  }, META_PAGE_VIEW_STABILIZATION_MS);
}

export function trackPageView(pagePath: string): void {
  const sanitizedPagePath = sanitizeAnalyticsPagePath(pagePath);

  if (!isBrowser()) {
    return;
  }

  if (hasCookieConsent('analytics') && lastTrackedGooglePagePath !== sanitizedPagePath) {
    lastTrackedGooglePagePath = sanitizedPagePath;
    trackGooglePageView(sanitizedPagePath);
  }

  trackMetaPageView(sanitizedPagePath);
  trackStartHubPixel('PageView', {}, undefined, sanitizedPagePath);
}

/** `_fbc` when the cookie exists, else built from `fbclid` in Meta's documented `fb.<index>.<ms>.<fbclid>` format. */
function readMetaClickIdentifier(): string | undefined {
  const cookie = readPixelCookie('_fbc');
  if (cookie && cookie.length <= 200) return cookie;
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  if (!fbclid || fbclid.length > 200) return undefined;
  return `fb.${META_FBC_SUBDOMAIN_INDEX}.${Date.now()}.${fbclid}`;
}

/**
 * `_fbp` as fbevents.js keeps it. A bare `init` does not create the cookie, only the first event does, and the
 * beacon is now that first event, so it makes the cookie in Meta's `fb.<index>.<ms>.<random>` format; fbevents.js
 * reads an existing one instead of minting another. Returns undefined when the browser refuses the write.
 */
function ensureMetaBrowserId(): string | undefined {
  const existing = readPixelCookie('_fbp');
  if (existing && existing.length <= 200) return existing;
  const [random] = crypto.getRandomValues(new Uint32Array(1));
  const value = `fb.${META_FBC_SUBDOMAIN_INDEX}.${Date.now()}.${Math.floor((random / 2 ** 32) * META_FBP_RANDOM_RANGE)}`;
  const hostname = window.location.hostname;
  const onRootDomain = hostname === COOKIE_DOMAIN_ROOT || hostname.endsWith(`.${COOKIE_DOMAIN_ROOT}`);
  try {
    const written = setCookie('_fbp', value, {
      ...(onRootDomain ? { domain: `.${COOKIE_DOMAIN_ROOT}` } : {}),
      maxAgeSeconds: META_FBP_MAX_AGE_SECONDS,
      sameSite: 'Lax',
    });
    return written && readPixelCookie('_fbp') === value ? value : undefined;
  } catch {
    return undefined;
  }
}

function readMetaBeaconReferrer(): string | undefined {
  if (!document.referrer) return undefined;
  try {
    const referrer = new URL(document.referrer);
    if (!['http:', 'https:'].includes(referrer.protocol)) return undefined;
    const value = `${referrer.origin}${sanitizeAnalyticsPathname(referrer.pathname)}`;
    return value.length <= 2048 ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * First Meta PageView of a document load, sent as Meta's image pixel instead of waiting for fbevents.js (about
 * 263 KB) behind the idle gate. Same gating as the script path: marketing consent (GPC already folded in by
 * hasCookieConsent) and a pixel-enabled app. The later fbq path skips this page, because Meta does not deduplicate
 * two browser events that share an eventID, so the page is recorded here and only here.
 */
export function sendMetaFirstPageViewBeacon(): boolean {
  if (
    !(isBrowser() && metaPixelEnabled && hasCookieConsent('marketing')) ||
    lastTrackedMetaPagePath !== undefined ||
    metaBeaconImage
  ) {
    return false;
  }
  const pagePath = getCurrentAnalyticsPagePath();
  const params = new URLSearchParams({
    id: META_PIXEL_ID,
    ev: 'PageView',
    dl: getAnalyticsPageLocation(pagePath),
    eid: crypto.randomUUID(),
    noscript: '1',
  });
  const referrer = readMetaBeaconReferrer();
  if (referrer) params.set('rl', referrer);
  const fbp = ensureMetaBrowserId();
  if (fbp) params.set('fbp', fbp);
  const fbc = readMetaClickIdentifier();
  if (fbc) params.set('fbc', fbc);
  const image = new Image();
  image.src = `${META_IMAGE_PIXEL_URL}?${params.toString()}`;
  metaBeaconImage = image;
  metaBeaconPathname = getAnalyticsPathAndSearch(pagePath).pathname;
  return true;
}

export function useTrackPageView(pagePath: string, measurementId?: string): void {
  const context = use(AnalyticsContext);
  const resolvedMeasurementId = measurementId ?? context?.measurementId;
  const metaEnabled = context?.metaPixelEnabled ?? false;
  const hasAnalyticsConsent = useCookieConsent('analytics');
  const hasMarketingConsent = useCookieConsent('marketing');

  useEffect(() => {
    const sync = () => {
      syncStartHubPixelConsent();
      if (!hasCookieConsent('marketing')) {
        if (pendingMetaPageViewTimer) clearTimeout(pendingMetaPageViewTimer);
        pendingMetaPageViewTimer = undefined;
        pendingMetaPagePath = undefined;
        pendingRegistrationEventId = undefined;
        pendingRegistrationCategory = undefined;
        lastTrackedMetaPagePath = undefined;
        lastTrackedMetaContentKey = undefined;
        metaBeaconImage = undefined;
        metaBeaconPathname = undefined;
      }
      if (!hasCookieConsent('analytics')) lastTrackedGooglePagePath = undefined;
      if (hasCookieConsent('analytics') || hasCookieConsent('marketing')) trackPageView(pagePath);
    };
    const unsubscribe = subscribeToConsent(sync);
    window.addEventListener('focus', sync);
    return () => {
      unsubscribe();
      window.removeEventListener('focus', sync);
    };
  }, [pagePath]);

  useEffect(() => {
    configureMetaPixel(metaEnabled);
    if (resolvedMeasurementId && hasAnalyticsConsent) {
      requestBrowserAnalytics(resolvedMeasurementId);
    }

    if (hasAnalyticsConsent || hasMarketingConsent) {
      trackPageView(pagePath);
    }
  }, [hasAnalyticsConsent, hasMarketingConsent, metaEnabled, pagePath, resolvedMeasurementId]);
}

let sensitiveRecordingMaskHolders = 0;

/**
 * Masks the page while the visitor is somewhere a recording must not see.
 *
 * The start-up check only decides whether a recording begins. These apps route
 * on the client, so a visitor who lands somewhere ordinary starts one and then
 * walks into billing with it still running, and Clarity has no command that
 * stops a recording. Masking is what is left.
 *
 * In the commit rather than after it, so the attribute is on the page by the
 * time the observer watching that commit is called.
 *
 * Keyed on whether the page is sensitive, not on which page it is: billing to
 * checkout would otherwise drop the mask and re-add it around the commit that
 * renders the next set of amounts.
 *
 * Counted because the attribute is a single global and this hook is shared —
 * whoever leaves last clears it, so one caller cannot strip a mask another
 * still needs.
 */
export function useSensitiveRecordingMask(pagePath: string): void {
  const isSensitive = hasSensitiveRecordingPagePath(pagePath);

  useLayoutEffect(() => {
    if (!isSensitive) {
      return;
    }

    sensitiveRecordingMaskHolders += 1;
    document.body.setAttribute(CLARITY_MASK_ATTRIBUTE, 'true');

    return () => {
      sensitiveRecordingMaskHolders -= 1;
      if (sensitiveRecordingMaskHolders === 0) {
        document.body.removeAttribute(CLARITY_MASK_ATTRIBUTE);
      }
    };
  }, [isSensitive]);
}

export function trackAnalyticsEvent<TName extends AnalyticsEventName>(
  name: TName,
  properties: AnalyticsEventPayloads[TName],
  options?: { metaEventId?: string; onMetaQueued?: () => void },
): void {
  if (!isBrowser()) {
    return;
  }

  const cleanedProperties = cleanAnalyticsProperties(properties);

  trackMetaAnalyticsEvent(name, cleanedProperties, options?.metaEventId, options?.onMetaQueued);
  const pixelEvent = START_HUB_EVENT_NAMES[name];
  // MBA checkout_started follows a paid_plan_selected click that already reported InitiateCheckout. A second one
  // under a random id would reach Meta as another event. Mentor bookings have no other source.
  const duplicatesMbaClick = name === 'checkout_started' && cleanedProperties.checkout_type === 'mba';
  if (pixelEvent && !duplicatesMbaClick && (name !== 'signup_completed' || options?.metaEventId)) {
    trackStartHubPixel(pixelEvent, cleanedProperties, options?.metaEventId);
  }

  if (!hasCookieConsent('analytics')) {
    return;
  }

  // An event may precede a full-page redirect, so it loads GA now rather than waiting for the idle slot.
  flushBrowserAnalytics();
  if (initializedMeasurementId) {
    ReactGA.event(name, cleanedProperties);
    return;
  }

  window.gtag?.('event', name, cleanedProperties);
}

/**
 * Sends one signup or checkout step to Marketing OS under the same visitor and session as page views, through the
 * same consent gate and sanitizing. Resolves false when nothing was sent (no consent, another host, request failed).
 * Steps are Marketing OS only: GA and Meta keep their own events.
 */
export function trackFunnelStep<TStep extends FunnelStepName>(
  step: TStep,
  properties: FunnelStepPayloads[TStep],
  eventId?: string,
): Promise<boolean> {
  if (!isBrowser()) return Promise.resolve(false);
  return trackStartHubPixel(step, cleanAnalyticsProperties(properties), eventId);
}

/** How long a queued Meta event gets to leave the page before a full-page redirect. */
export const META_NAVIGATION_GRACE_MS = 300;

export interface MetaNavigationGate {
  /** Pass as `onMetaQueued` to the `trackAnalyticsEvent` call that precedes the navigation. */
  onMetaQueued: () => void;
  /** Resolves once a queued Meta event had its grace period; immediately when nothing was queued. */
  settle: () => Promise<void>;
}

/**
 * For an event immediately followed by leaving the site, such as the redirect
 * to Stripe. A Pixel call made right before `window.location` changes can be
 * cancelled with the page, so the redirect waits briefly when Meta actually
 * queued something and not at all otherwise.
 */
export function createMetaNavigationGate(): MetaNavigationGate {
  let metaQueued = false;
  return {
    onMetaQueued: () => {
      metaQueued = true;
    },
    settle: () =>
      metaQueued
        ? new Promise<void>(resolve => {
            setTimeout(resolve, META_NAVIGATION_GRACE_MS);
          })
        : Promise.resolve(),
  };
}

export function identifyAnalyticsUser(userId: string): void {
  if (!(isBrowser() && hasCookieConsent('analytics'))) {
    return;
  }

  if (initializedMeasurementId) {
    ReactGA.set({ user_id: userId });
    return;
  }
  if (queueGoogleCall(() => ReactGA.set({ user_id: userId }))) return;

  window.gtag?.('set', { user_id: userId });
}

const sendLegacyAnalyticsEvent = ({ category, action, label, value }: AnalyticsEvent): void => {
  if (!(isBrowser() && hasCookieConsent('analytics'))) {
    return;
  }

  flushBrowserAnalytics();
  ReactGA.event({ category, action, label, value });
};

export function useAnalytics() {
  const context = use(AnalyticsContext);
  const measurementId = context?.measurementId;
  const hasAnalyticsConsent = useCookieConsent('analytics');

  useEffect(() => {
    if (measurementId && hasAnalyticsConsent) {
      requestBrowserAnalytics(measurementId);
    }
  }, [hasAnalyticsConsent, measurementId]);

  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }

  return {
    identifyUser: identifyAnalyticsUser,
    sendEvent: sendLegacyAnalyticsEvent,
    sendPageView: trackPageView,
    trackEvent: trackAnalyticsEvent,
  };
}
